import React from "react";
import { useQuery } from "@apollo/client";
import { Redirect, useLocation } from "react-router-dom";
import { GET_ONBOARDING_STATUS, OnboardingStatusData } from "./queries";
import { FullPageLoader } from "../../../middleware/loaders";

export type OnboardingStatus = {
  hasBranch: boolean;
  hasProducts: boolean;
  hasBrand: boolean;
  hasDeliveryConfig: boolean;
  hasTablesConfig: boolean;
  hasPayments: boolean;
};

function getSettingsFromStorage(): Record<string, any> {
  try {
    return JSON.parse(localStorage.getItem("settings") || "{}");
  } catch {
    return {};
  }
}

export function computeOnboardingStatus(
  data: OnboardingStatusData
): OnboardingStatus {
  const settings = getSettingsFromStorage();
  return {
    hasBranch: data.getBranches.length > 0,
    hasProducts: data.getProductCount > 0,
    hasBrand:
      data.getPartner.logo !== null && data.getPartner.short_desc !== null,
    hasDeliveryConfig: settings.disable_delivery !== undefined,
    hasTablesConfig: settings.has_tables !== undefined,
    hasPayments: data.getConnectAccount !== null,
  };
}

function isOnboardingComplete(status: OnboardingStatus): boolean {
  return (
    status.hasBranch &&
    status.hasProducts &&
    status.hasBrand &&
    status.hasDeliveryConfig &&
    status.hasTablesConfig
  );
}

const ALLOWED_PATHS = ["/welcome", "/logout"];

// Paths the user needs to visit during onboarding steps
const ONBOARDING_PATHS = [
  "/branches/create",
  "/branches",
  "/products/new",
  "/products",
  "/account",
  "/payments",
];

function isPathAllowedDuringOnboarding(pathname: string): boolean {
  if (ALLOWED_PATHS.some((p) => pathname === p)) return true;
  if (ONBOARDING_PATHS.some((p) => pathname.startsWith(p))) return true;
  return false;
}

type Props = {
  children: React.ReactNode;
};

const OnboardingGuard: React.FC<Props> = ({ children }) => {
  const location = useLocation();

  // Subusers skip onboarding — it's a partner-level setup
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const isSubuser = userData.subuser_id && userData.subuser_id > 0;

  const { data, loading } = useQuery<OnboardingStatusData>(
    GET_ONBOARDING_STATUS,
    { fetchPolicy: "cache-and-network", skip: isSubuser }
  );

  if (isSubuser) {
    return <>{children}</>;
  }

  if (loading && !data) {
    return <FullPageLoader show />;
  }

  if (!data) {
    // If query failed, don't block — let them through
    return <>{children}</>;
  }

  const status = computeOnboardingStatus(data);

  if (isOnboardingComplete(status)) {
    // If they're on /welcome but everything is done, let them through
    // (the welcome page itself will show success and redirect)
    return <>{children}</>;
  }

  // Onboarding incomplete — only allow certain paths
  if (!isPathAllowedDuringOnboarding(location.pathname)) {
    return <Redirect to="/welcome" />;
  }

  return <>{children}</>;
};

export default OnboardingGuard;
